import * as Sentry from '@sentry/react';

import { getConfig } from 'pkg/config/app';

import type * as models from 'pkg/api/models';
import { isBrowserSupported } from 'pkg/client_browser_info';
import { isBeta, isDev } from 'pkg/flags';
import { routePattern } from 'pkg/logger/route_pattern';
import { isAndroid, isIOS, isWeb } from 'pkg/platform';

let sentryEnabled = false;
let userContextSet = false;

// inits Sentry logger
function init() {
	let logLevels: string[] = [];
	if (isDev()) {
		logLevels = ['log', 'info', 'warn', 'assert'];
	} else if (isBeta()) {
		logLevels = ['warning'];
	}

	const sentryConfig: Sentry.BrowserOptions = {
		dsn: getConfig().sentryDsn,
		integrations: [
			Sentry.captureConsoleIntegration({
				levels: logLevels,
			}),
		],
		beforeSend: filterErrors,
	};

	if (getConfig().version) {
		sentryConfig.release = getConfig().version;

		// iOS and Android emit different bundles under the same release, so the
		// sourcemaps must be uploaded and looked up per platform.
		if (isIOS()) {
			sentryConfig.dist = 'ios';
		} else if (isAndroid()) {
			sentryConfig.dist = 'android';
		}
	}

	if (getConfig().sentryDsn) {
		sentryEnabled = true;
		Sentry.init(sentryConfig);

		Sentry.addBreadcrumb({
			category: 'sentry',
			message: 'Sentry initialized',
			level: 'info',
			data: {
				version: getConfig().version,
			},
		});

		reportWebViewCrashRecovery();
	}
}

/**
 * Errors we never act on. They are not bugs — either the user cancelled
 * something, a platform API is behaving as documented, or a third party
 * injected script into our page.
 *
 * Matched against both the exception type and its message, since platform
 * errors frequently arrive as a generic `Error` with the real name inside
 * the message (`Error: AbortError: The operation was aborted.`).
 */
const ignoredErrorPatterns: string[] = [
	// Fetch aborted by us or by the browser, and network errors we cannot act on.
	'AbortError',
	'Failed to fetch',
	'NetworkError when attempting to fetch resource',
	'Load failed',

	// User tapped "Cancel" in the native or web share sheet.
	'Share canceled',
	'Abort due to cancellation of share',

	// Autoplay blocked by the browser. video.js drives autoplay through its own
	// middleware rather than our controller, so this cannot be caught at the
	// call site the way the other playback rejections are.
	//
	// @NOTE Wider than that one case — clipboard, fullscreen and geolocation
	// throw the same name — so anything of ours that can raise it must handle
	// it locally, since it will not show up here. See pkg/copyString.
	'NotAllowedError',

	// Playback races from scrubbing: a play() promise superseded by pause()
	// or by a new source. Playback recovers on its own.
	'request was interrupted',
	'interrupted by a new load request',

	// iOS did not hand back an APNS token, typically because the user has
	// notifications disabled at the OS level.
	'capacitorDidRegisterForRemoteNotifications not called',
];

/**
 * Ignored in web builds only. In the app, window.webkit is the Capacitor
 * bridge, and a failure to reach it is something we want to see.
 */
const ignoredWebOnlyErrorPatterns: string[] = [
	// Injected by the Facebook and Instagram in-app browsers on public pages,
	// reaching for their own native host. Not our code.
	'window.webkit.messageHandlers',
];

function isIgnoredError(type: string, message: string): boolean {
	const patterns = isWeb()
		? [...ignoredErrorPatterns, ...ignoredWebOnlyErrorPatterns]
		: ignoredErrorPatterns;

	return patterns.some(
		(pattern) => type.includes(pattern) || message.includes(pattern)
	);
}

function filterErrors(event: Sentry.ErrorEvent) {
	if (!isBrowserSupported(navigator.userAgent)) {
		return null;
	}

	if (event.exception && event.exception.values) {
		const shouldIgnore = event.exception.values.some((ex) =>
			isIgnoredError(ex.type || '', ex.value || '')
		);

		if (shouldIgnore) {
			return null;
		}
	}

	// Events sent through captureMessage carry no exception, only a message.
	if (event.message && isIgnoredError('', event.message)) {
		return null;
	}

	return event;
}

interface ErrorInfo {
	[key: string]: unknown;
}

function captureMessage(
	msg: string | Error,
	level: 'fatal' | 'error' | 'warning' | 'log' | 'info' | 'debug',
	data?: ErrorInfo
) {
	Sentry.addBreadcrumb({
		category: 'log',
		message: typeof msg === 'string' ? msg : msg.message,
		level,
		data,
	});

	// only send errors to Sentry
	if (sentryEnabled && ['fatal', 'error', 'warning'].includes(level)) {
		Sentry.withScope((s) => {
			if (data) {
				s.setContext('Log Info', data);
			}
			if (msg instanceof Error) {
				Sentry.captureException(msg);
			} else {
				Sentry.captureMessage(msg, level);
			}
		});

		return;
	}

	if (!isDev()) {
		return;
	}

	switch (level) {
		case 'debug':
			console.debug(msg, data);
			break;
		case 'warning':
			console.warn(msg, data);
			break;
		case 'error':
			console.error(msg, data);
			break;
		default:
			console.info(msg, data);
			break;
	}
}

function setUserContext(identity: string) {
	if (!sentryEnabled) {
		return;
	}

	Sentry.setUser({
		id: identity,
	});

	Sentry.addBreadcrumb({
		category: 'auth',
		message: `User set: ${identity}`,
		level: 'info',
	});

	userContextSet = true;
	flushWebViewCrashRecovery('user');
}

function setOrganizationContext(organization: models.group.Group) {
	if (!sentryEnabled) {
		return;
	}

	Sentry.setTag('organization.id', organization.id);
	Sentry.setTag('organization.name', organization.name);
}

function debug(msg: string, data?: ErrorInfo) {
	captureMessage(msg, 'debug', data);
}

function message(msg: string, data?: ErrorInfo) {
	captureMessage(msg, 'info', data);
}

function warning(msg: string, data?: ErrorInfo) {
	captureMessage(msg, 'warning', data);
}

function error(err: Error, data?: ErrorInfo) {
	captureMessage(err, 'error', data);
}

/**
 * Sets the crash scope for Sentry reporting.
 * - view: The crash occurred in a view. This is a recoverable error for the user by navigating away from the current page.
 * - full: The crash occurred in the full app, hard to recover from for a user.
 * - partial: The crash occurred in a component, and doesn't crash the entire view or page.
 *
 * @param crashScope - The scope of the crash, can be 'view', 'full', or 'partial'.
 * @returns A function that sets the crash scope in the provided Sentry scope.
 */
function setCrashScope(
	crashScope: 'view' | 'full' | 'partial'
): (
	scope: Sentry.Scope,
	error: unknown,
	componentStack: string | undefined
) => void {
	return (scope: Sentry.Scope) => {
		scope.setTag('crash_scope', crashScope);
		switch (crashScope) {
			case 'view':
				scope.setLevel('error');
				break;
			case 'full':
				scope.setLevel('fatal');
				break;
			case 'partial':
				scope.setLevel('warning');
				break;
		}
	};
}

type WebViewCrashRecovery = NonNullable<Window['__WEBVIEW_CRASH_RECOVERY']>;

// Stays 'important' in the background too (no setRendererPriorityPolicy); use appVisible for that.
const RENDERER_PRIORITY: Record<number, string> = {
	0: 'waived',
	1: 'bound',
	2: 'important',
};

// Held until the user is known; the timeout sends it anonymously if none arrives.
let pendingRecovery: WebViewCrashRecovery | null = null;
let recoveryFlushTimer: ReturnType<typeof setTimeout> | null = null;

const RECOVERY_FLUSH_TIMEOUT_MS = 15_000;

function toGigabytes(bytes?: number): string {
	if (!bytes) {
		return 'unknown';
	}

	// Half-gigabyte buckets are enough to separate device tiers.
	return (Math.round(bytes / 2 ** 29) / 2).toFixed(1);
}

function flushWebViewCrashRecovery(trigger: 'user' | 'timeout') {
	if (!pendingRecovery) {
		return;
	}

	const recovery = pendingRecovery;
	pendingRecovery = null;

	if (recoveryFlushTimer) {
		clearTimeout(recoveryFlushTimer);
		recoveryFlushTimer = null;
	}

	const priority =
		recovery.rendererPriority !== undefined
			? (RENDERER_PRIORITY[recovery.rendererPriority] ?? 'unknown')
			: 'unknown';

	Sentry.withScope((scope) => {
		scope.setTag('webview_crash_recovery', 'true');
		scope.setTag('webview_did_crash', String(recovery.didCrash));
		scope.setTag('webview_renderer_priority', priority);
		scope.setTag(
			'webview_app_visible',
			recovery.appVisible === undefined
				? 'unknown'
				: String(recovery.appVisible)
		);
		scope.setTag(
			'webview_kill_route',
			recovery.url ? routePattern(recovery.url) : 'unknown'
		);
		scope.setTag(
			'webview_route_restored',
			recovery.routeRestored === undefined
				? 'unknown'
				: String(recovery.routeRestored)
		);
		scope.setTag('webview_report_trigger', trigger);
		scope.setTag('device_total_mem_gb', toGigabytes(recovery.totalMem));
		scope.setTag(
			'device_low_ram',
			recovery.lowRamDevice === undefined
				? 'unknown'
				: String(recovery.lowRamDevice)
		);
		scope.setContext('WebView Crash', {
			didCrash: recovery.didCrash,
			reason: recovery.didCrash ? 'renderer_crash' : 'system_oom_kill',
			crashTimestamp: new Date(recovery.timestamp).toISOString(),
			rendererPriority: priority,
			appVisible: recovery.appVisible,
			routeRestored: recovery.routeRestored,
			systemLowMemory: recovery.lowMemory,
			availMemMb: recovery.availMem && Math.round(recovery.availMem / 2 ** 20),
			totalMemMb: recovery.totalMem && Math.round(recovery.totalMem / 2 ** 20),
			lowRamDevice: recovery.lowRamDevice,
			memoryClassMb: recovery.memoryClassMb,
			// Chromium only, capped at 8.
			jsDeviceMemoryGb: (navigator as { deviceMemory?: number }).deviceMemory,
		});
		Sentry.captureMessage(
			`WebView render process ${recovery.didCrash ? 'crashed' : 'killed by system'} — app recovered`,
			'warning'
		);
	});
}

function queueWebViewCrashRecovery(recovery: WebViewCrashRecovery) {
	pendingRecovery = recovery;

	if (userContextSet) {
		flushWebViewCrashRecovery('user');
		return;
	}

	if (!recoveryFlushTimer) {
		recoveryFlushTimer = setTimeout(
			() => flushWebViewCrashRecovery('timeout'),
			RECOVERY_FLUSH_TIMEOUT_MS
		);
	}
}

function reportWebViewCrashRecovery() {
	const recovery = window.__WEBVIEW_CRASH_RECOVERY;

	if (recovery) {
		delete window.__WEBVIEW_CRASH_RECOVERY;
		queueWebViewCrashRecovery(recovery);
	}

	// The native side can inject after init(); catch the late assignment here.
	Object.defineProperty(window, '__WEBVIEW_CRASH_RECOVERY', {
		configurable: true,
		get: () => undefined,
		set: (value: WebViewCrashRecovery | undefined) => {
			if (value) {
				queueWebViewCrashRecovery(value);
			}
		},
	});
}

export {
	init,
	setUserContext,
	setOrganizationContext,
	debug,
	message,
	warning,
	error,
	setCrashScope,
};
